import React from 'react';
import { PipelineNodeType, PipelineNodeTemplate } from '../../types/pipeline';

export const PIPELINE_NODE_TEMPLATES: Record<PipelineNodeType, PipelineNodeTemplate> = {
  api_endpoint: {
    type: 'api_endpoint',
    name: 'API Endpoint',
    icon: '🌐',
    description: 'Call an external API endpoint',
    defaultConfig: {
      url: 'https://api.example.com/endpoint',
      method: 'GET',
      headers: {},
      params: {},
      timeout: 30000,
    },
    inputPorts: [{ id: 'trigger', label: 'Trigger', type: 'trigger' }],
    outputPorts: [
      { id: 'success', label: 'Success', type: 'response' },
      { id: 'error', label: 'Error', type: 'error' },
    ],
  },
  model: {
    type: 'model',
    name: 'Model',
    icon: '🤖',
    description: 'Run an inference model',
    defaultConfig: {
      modelId: '',
      modelName: '',
      version: '1.0',
      params: {},
    },
    inputPorts: [
      { id: 'input', label: 'Input Data', type: 'data' },
      { id: 'trigger', label: 'Trigger', type: 'trigger' },
    ],
    outputPorts: [
      { id: 'output', label: 'Output', type: 'data' },
      { id: 'error', label: 'Error', type: 'error' },
    ],
  },
  logic_if: {
    type: 'logic_if',
    name: 'If Condition',
    icon: '❓',
    description: 'Conditional branching',
    defaultConfig: {
      condition: '',
      operator: '==',
      value: '',
    },
    inputPorts: [
      { id: 'trigger', label: 'Trigger', type: 'trigger' },
      { id: 'variable', label: 'Variable', type: 'data' },
    ],
    outputPorts: [
      { id: 'true', label: 'True', type: 'trigger' },
      { id: 'false', label: 'False', type: 'trigger' },
    ],
  },
  logic_switch: {
    type: 'logic_switch',
    name: 'Switch',
    icon: '🔀',
    description: 'Multi-way branching',
    defaultConfig: {
      variable: '',
      cases: [
        { value: 'case1', label: 'Case 1' },
        { value: 'case2', label: 'Case 2' },
      ],
      default: 'default',
    },
    inputPorts: [
      { id: 'trigger', label: 'Trigger', type: 'trigger' },
      { id: 'variable', label: 'Variable', type: 'data' },
    ],
    outputPorts: [
      { id: 'case1', label: 'Case 1', type: 'trigger' },
      { id: 'case2', label: 'Case 2', type: 'trigger' },
      { id: 'default', label: 'Default', type: 'trigger' },
    ],
  },
  logic_loop: {
    type: 'logic_loop',
    name: 'Loop',
    icon: '🔄',
    description: 'Iterate over data',
    defaultConfig: {
      iterationVariable: 'item',
      arrayVariable: '',
      maxIterations: 1000,
    },
    inputPorts: [
      { id: 'trigger', label: 'Trigger', type: 'trigger' },
      { id: 'array', label: 'Array', type: 'data' },
    ],
    outputPorts: [
      { id: 'loop', label: 'Loop Body', type: 'trigger' },
      { id: 'complete', label: 'Complete', type: 'trigger' },
    ],
  },
  transformer: {
    type: 'transformer',
    name: 'Transformer',
    icon: '🔄',
    description: 'Transform data',
    defaultConfig: {
      transformationType: 'map',
      code: '',
      mapping: {},
    },
    inputPorts: [
      { id: 'input', label: 'Input', type: 'data' },
      { id: 'trigger', label: 'Trigger', type: 'trigger' },
    ],
    outputPorts: [
      { id: 'output', label: 'Output', type: 'data' },
      { id: 'error', label: 'Error', type: 'error' },
    ],
  },
  validator: {
    type: 'validator',
    name: 'Validator',
    icon: '✓',
    description: 'Validate data',
    defaultConfig: {
      schema: {},
      rules: [],
    },
    inputPorts: [
      { id: 'input', label: 'Input', type: 'data' },
      { id: 'trigger', label: 'Trigger', type: 'trigger' },
    ],
    outputPorts: [
      { id: 'valid', label: 'Valid', type: 'trigger' },
      { id: 'invalid', label: 'Invalid', type: 'trigger' },
    ],
  },
  output: {
    type: 'output',
    name: 'Output',
    icon: '📤',
    description: 'Final output',
    defaultConfig: {
      format: 'json',
      fields: [],
    },
    inputPorts: [
      { id: 'input', label: 'Input', type: 'data' },
      { id: 'trigger', label: 'Trigger', type: 'trigger' },
    ],
    outputPorts: [],
  },
};

interface PaletteSection {
  title: string;
  types: PipelineNodeType[];
}

// For now, only show model and api_endpoint nodes (simple start)
const PALETTE_SECTIONS: PaletteSection[] = [
  {
    title: 'Available Nodes',
    types: ['api_endpoint', 'model'],
  },
  // Logic and other nodes hidden for initial implementation
  // {
  //   title: 'Logic',
  //   types: ['logic_if', 'logic_switch', 'logic_loop'],
  // },
  // {
  //   title: 'Data Processing',
  //   types: ['transformer', 'validator'],
  // },
];

export const PipelinePalette: React.FC = () => {
  const handleDragStart = (e: React.DragEvent, nodeType: PipelineNodeType) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('nodeType', nodeType);
  };

  return (
    <div className="pipeline-palette">
      <div className="palette-header">
        <h3>Node Types</h3>
      </div>

      <div className="palette-content">
        {PALETTE_SECTIONS.map((section) => (
          <div key={section.title} className="palette-section">
            <div className="section-title">{section.title}</div>
            {section.types.map((nodeType) => {
              const template = PIPELINE_NODE_TEMPLATES[nodeType];
              return (
                <div
                  key={nodeType}
                  className="palette-item"
                  draggable
                  onDragStart={(e) => handleDragStart(e, nodeType)}
                  title={template.description}
                >
                  <span className="item-icon">{template.icon}</span>
                  <span className="item-name">{template.name}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PipelinePalette;
